import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";
import {
	ClientFinancialPlanExpenseNameEnum,
	ClientFinancialPlanExpenseTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { z } from "zod";

export const getExpenseFormSchema = () =>
	z.object({
		expenses: z.array(
			z.object({
				type: z.nativeEnum(ClientFinancialPlanExpenseTypeEnum),
				name: z.nativeEnum(ClientFinancialPlanExpenseNameEnum),
				monthlyPayment: z.number().min(0),
				sustainableAmount: z.number().min(0),
			}),
		),
		totalValue: z.number().optional(),
		exact: z.boolean().optional(),
	});

export type ExpenseFormModel = z.infer<ReturnType<typeof getExpenseFormSchema>>;

export const useExpenseForm = () => {
	const [finance, setFinance] = useAtom(financeState);

	const isExact =
		(finance?.expenses.length > 0 &&
			finance?.expenses[0].type !== ClientFinancialPlanExpenseTypeEnum.Other &&
			finance?.expenses[0].name !== ClientFinancialPlanExpenseNameEnum.Other) ||
		finance?.expenses.length > 1;

	const form = useForm<ExpenseFormModel>({
		resolver: zodResolver(getExpenseFormSchema()),
		values: {
			expenses: finance?.expenses ?? [],
			totalValue: finance?.expenses.reduce(
				(acc, expense) => acc + expense.monthlyPayment,
				0,
			),
			exact: isExact,
		},
	});

	const { setValue, control } = form;

	const {
		fields: expensesFields,
		append: expensesAppend,
		remove: expensesRemove,
	} = useFieldArray({
		control,
		name: "expenses",
	});

	const onSubmit = form.handleSubmit((data) => {
		if (!data.exact) {
			setFinance({
				...finance,
				expenses: [
					{
						type: ClientFinancialPlanExpenseTypeEnum.Other,
						name: ClientFinancialPlanExpenseNameEnum.Other,
						monthlyPayment: data.totalValue ?? 0,
						sustainableAmount: 0,
					},
				],
			});
			return;
		}
		setFinance({
			...finance,
			expenses: data.expenses,
		});
	});

	const watchedExpenses = useWatch({ control, name: "expenses" });
	useEffect(() => {
		const sum =
			(watchedExpenses ?? []).reduce((acc, curr) => {
				const n = Number.parseFloat(
					String(curr?.monthlyPayment ?? "0").replace(",", "."),
				);
				return acc + (Number.isNaN(n) ? 0 : n);
			}, 0) ?? 0;
		setValue("totalValue", sum);
	}, [watchedExpenses, setValue]);

	return {
		onSubmit,
		form,
		setValue,
		control,
		expensesFields,
		expensesAppend,
		expensesRemove,
	};
};
